import { motion } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { TextField } from "@mui/material";
import * as Yup from "yup";
import Images from "../../Images";
import { useMutation } from "react-query";
import { requestPasswordReset } from "../../requests/auth/Authentication";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

// * Request Validation
const validationSchema = Yup.object({
  email: Yup.string()
    .required("Email is required")
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid Email Entered"),
});

const initialValues = {
  email: "",
};

function ForgotPasswordEmail() {
  const navigate = useNavigate();

  const resetPasswordMutation = useMutation(requestPasswordReset, {
    onSuccess: (data) => {
      toast.success(data.reason || "OTP sent successfully");
      navigate("/password-reset");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to send OTP");
    },
  });

  return (
    <div className="h-screen w-full flex flex-col sm:flex-row bg-[#FEFEFE]">
      {/* Left Side */}
      <motion.div
        className="relative hidden w-full h-full sm:w-1/2 sm:flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#FF6C6C] via-[#FF8B8B] to-[#FF9B9B]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Animated Background Elements */}
        <motion.div
          className="absolute w-[800px] h-[800px] bg-white/10 rounded-full blur-xl"
          initial={{ scale: 0, rotate: 0 }}
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />

        {/* Floating Elements */}
        <motion.div
          className="absolute top-10 right-10 bg-white/30 backdrop-blur-md rounded-2xl p-4 shadow-lg z-20"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse"></div>
            <span className="text-white text-sm font-medium">Secure Recovery</span>
          </div>
        </motion.div>

        <motion.div
          className="absolute bottom-20 left-10 bg-white/30 backdrop-blur-md rounded-2xl p-4 shadow-lg z-20"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-yellow-400 animate-pulse"></div>
            <span className="text-white text-sm font-medium">Quick Password Reset</span>
          </div>
        </motion.div>

        {/* Main Image Container */}
        <motion.div
          className="relative z-10 w-4/5 h-4/5"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <motion.div
            className="relative w-full h-full rounded-2xl overflow-hidden shadow-2xl bg-white/10 backdrop-blur-sm"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <motion.img
              src={Images.register_second}
              alt="forgot-password"
              className="object-cover w-full h-full rounded-2xl hover:scale-110 transition-transform duration-700"
            />
            
            {/* Content Overlay */}
            <motion.div 
              className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/60 flex flex-col justify-end items-center pb-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <motion.div className="text-center px-8">
                <motion.h2 
                  className="text-5xl font-bold mb-6 text-white drop-shadow-lg"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.2 }}
                >
                  Password Recovery
                </motion.h2>
                <motion.p 
                  className="text-xl text-white/90 max-w-md mx-auto"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.4 }}
                >
                  We'll help you get back to your account
                </motion.p>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Right Side */}
      <motion.div 
        className="w-full px-8 pt-16 pb-8 overflow-y-auto sm:w-1/2 sm:h-screen sm:px-16"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Logo */}
        <motion.div
          className="mb-6"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <img src={Images.logo} alt="Dealboxx Logo" className="w-32 h-auto"/>
        </motion.div>

        {/* Back Button */}
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <motion.button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#808080] hover:text-[#FF6C6C] transition-colors duration-300"
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M15 19l-7-7 7-7" 
              />
            </svg>
            <span className="font-medium">Back to Login</span>
          </motion.button>
        </motion.div>

        {/* Title and Description */}
        <motion.div
          className="space-y-3 mb-8"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] bg-clip-text text-transparent">
            Forgot Password?
          </h1>
          <p className="text-[#808080] text-base">
            Don't worry! Enter your registered email below to receive password reset instructions.
          </p>
        </motion.div>

        {/* Form */}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            resetPasswordMutation.mutate(values);
          }}
        >
          {({ handleSubmit, touched, errors }) => (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              <Form className="space-y-6">
                {/* Email Field */}
                <motion.div
                  whileHover={{ scale: 1.01 }}
                  className="relative"
                >
                  <Field
                    name="email"
                    as={TextField}
                    label="Email Address"
                    variant="outlined"
                    fullWidth
                    error={Boolean(errors.email && touched.email)}
                    helperText={
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-red-500 italic text-sm"
                      />
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'white',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                        '&.Mui-focused': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(255,108,108,0.2)',
                          '& fieldset': {
                            borderColor: '#FF6C6C',
                            borderWidth: '2px',
                          }
                        }
                      },
                      '& .MuiInputLabel-root': {
                        '&.Mui-focused': {
                          color: '#FF6C6C'
                        }
                      }
                    }}
                  />
                </motion.div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={resetPasswordMutation.isLoading}
                  className={`w-full bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] text-white font-semibold py-4 px-6 rounded-xl shadow-lg transition-all duration-300 ${
                    resetPasswordMutation.isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl'
                  }`}
                  whileHover={resetPasswordMutation.isLoading ? {} : { scale: 1.02 }}
                  whileTap={resetPasswordMutation.isLoading ? {} : { scale: 0.98 }}
                >
                  <div className="flex items-center justify-center gap-2">
                    {resetPasswordMutation.isLoading && (
                      <motion.div
                        className="w-5 h-5 border-3 border-white border-t-transparent rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                    )}
                    {resetPasswordMutation.isLoading ? "Sending OTP..." : "Receive OTP"}
                  </div>
                </motion.button>
              </Form>
            </motion.div>
          )}
        </Formik>

        {/* Feature Cards */}
        <motion.div
          className="mt-12 grid grid-cols-3 gap-3"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
        >
          <div className="text-center p-3 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">🔒</div>
              <div className="text-xs text-gray-600">Secure Reset</div>
            </motion.div>
          </div>
          <div className="text-center p-3 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">⚡</div>
              <div className="text-xs text-gray-600">Quick Process</div>
            </motion.div>
          </div>
          <div className="text-center p-3 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">📧</div>
              <div className="text-xs text-gray-600">Email Support</div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default ForgotPasswordEmail;
